import * as React from "react";
import { DismissRegular } from "@fluentui/react-icons";
import { getProjectSettings, setProjectSetting } from "../utils/projectSettings";

interface VideoModalProps {
    uri: string;
    label: string;
    onClose: () => void;
    /**
     * When provided, the chosen playback speed is remembered per-project (via
     * the shared project-settings store), so reopening any video in the same
     * project restores it.
     */
    projectId?: string;
}

// Native HTML5 <video controls> already exposes playback rate via the overflow
// (3-dot) menu in modern browsers, so we don't render our own speed UI here.
// We just apply the remembered (or default) rate on load and persist changes.
const DEFAULT_SPEED = 0.5;

function readSavedSpeed(projectId?: string): number {
    const saved = getProjectSettings(projectId).videoPlaybackRate;
    return typeof saved === "number" && Number.isFinite(saved) && saved > 0 ? saved : DEFAULT_SPEED;
}

const SPEED_EPSILON = 0.0001;

function ratesEqual(left: number, right: number): boolean {
    return Math.abs(left - right) < SPEED_EPSILON;
}

const VideoModal: React.FC<VideoModalProps> = ({ uri, label, onClose, projectId }) => {
    const videoRef = React.useRef<HTMLVideoElement>(null);
    const expectedPlaybackRateRef = React.useRef<number | null>(null);
    const hasAppliedInitialSpeedRef = React.useRef(false);
    const videoKeyRef = React.useRef(`${projectId ?? ""}\n${uri}`);
    const videoKey = `${projectId ?? ""}\n${uri}`;

    if (videoKeyRef.current !== videoKey) {
        videoKeyRef.current = videoKey;
        expectedPlaybackRateRef.current = null;
        hasAppliedInitialSpeedRef.current = false;
    }

    const applyInitialSpeed = () => {
        const video = videoRef.current;
        if (!video || hasAppliedInitialSpeedRef.current) return;

        const speed = readSavedSpeed(projectId);
        expectedPlaybackRateRef.current = speed;
        hasAppliedInitialSpeedRef.current = true;
        if (!ratesEqual(video.playbackRate, speed)) video.playbackRate = speed;
    };

    // Persist the user's chosen speed for this project whenever they change it
    // (the native overflow menu fires `ratechange`).
    const handleRateChange = () => {
        if (!videoRef.current) return;

        const playbackRate = videoRef.current.playbackRate;
        const expectedPlaybackRate = expectedPlaybackRateRef.current;
        if (expectedPlaybackRate !== null && ratesEqual(playbackRate, expectedPlaybackRate)) return;

        expectedPlaybackRateRef.current = playbackRate;
        if (projectId) setProjectSetting(projectId, "videoPlaybackRate", playbackRate);
    };

    return (
        <div
            style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.88)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
            onClick={onClose}
        >
            <div
                style={{ backgroundColor: "#161b22", border: "1px solid #30363d", borderRadius: 12, overflow: "hidden", width: "min(960px, 90vw)", maxHeight: "92vh", display: "flex", flexDirection: "column" }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header: title + close. Playback speed lives in the native video overflow menu. */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", borderBottom: "1px solid #30363d", gap: 12, flexShrink: 0 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#e6edf3", flex: "1 1 0", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {label}
                    </span>
                    <button
                        onClick={onClose}
                        style={{ border: "none", backgroundColor: "transparent", color: "#8b949e", cursor: "pointer", padding: "2px 6px", display: "flex", alignItems: "center", flexShrink: 0 }}
                        aria-label="Close"
                    ><DismissRegular style={{ fontSize: 16 }} /></button>
                </div>

                {/* Video — fills modal width; object-fit:contain letterboxes non-matching aspect ratios */}
                <video
                    ref={videoRef}
                    controls
                    autoPlay
                    style={{ display: "block", width: "100%", height: "auto", maxHeight: "calc(92vh - 50px)", objectFit: "contain", backgroundColor: "#000" }}
                    src={uri}
                    key={uri}
                    onLoadedMetadata={applyInitialSpeed}
                    onCanPlay={applyInitialSpeed}
                    onRateChange={handleRateChange}
                >
                    <track kind="captions" />
                </video>
            </div>
        </div>
    );
};

export default VideoModal;
